import JsCookie from "js-cookie";

import { COOKIE_KEYS_MAP } from "~/common/constants";

import adjustEventConfig, { AdjustConfigKey } from "./adjustEventConfig";
import fbPXEventConfig, { FbPXConfigKey } from "./fbPXEventConfig";
import gaEventConfig, { GAConfigKey } from "./gaEventConfig";
import { isoCurrencyConfig } from "./constant";
import { retryGetAdjustId } from "./utils";
import { PropertyType } from "./types";
import { reportAdjustRelative, RelativeType, RelativeCustomParamsType } from "./reportAdjustRelative";

const adjustAppCode = "9wp2lq38zr40";
const facebookPixelCode = "384171079919098";
const googleAnalyticsCode = "G-LRX5DQEQ9W";

const isProd = process.env.BUILD_ENV === "prod";

export function isValidKey(key: string | number | symbol, object: Record<string, unknown>): key is keyof typeof object {
  return key in object;
}

export { RelativeType };

// ga 的可以直接用，框架已经初始化了，在 next.config.js里面。

const Analytics = {
  debug: false,

  isInitAdjust: false,
  isInitFbPX: false,
  isInitGA: false,

  // 用户 ID，登录后才有
  uid: "",
  // adjust 的 adid
  // FIXME: 暂未保证 初始化顺序，如果是初始化获取  adjust_id， deviceId 很有可能拿不到。请注意。
  adjust_id: "",
  // adjust 的 uuid
  deviceId: "",

  // 获取 adjust 的 adid
  getAdjustId(): Promise<string> {
    return new Promise((resolve) => (this.adjust_id ? resolve(this.adjust_id) : retryGetAdjustId(resolve)));
  },

  eventTasks: [] as { eventName: AdjustConfigKey | FbPXConfigKey | GAConfigKey; eventProperties: PropertyType }[],

  reportAdjustRelative(type: RelativeType, params: Omit<RelativeCustomParamsType, "uid" | "web_uuid">): void {
    // 未初始化 sdk，也没选中 接受 cookie 类型，就过滤掉。
    if (!this.isInitAdjust && !this.checkInitCookieKey()) return;
    const attribution = window.Adjust?.getAttribution?.();
    attribution &&
      reportAdjustRelative(type, { ...attribution, web_uuid: this.deviceId, uid: this.uid || "", ...params });
  },

  initAdjust(): void {
    if (this.isInitAdjust) return;
    require("./adjust");
    window.Adjust?.initSdk({
      appToken: adjustAppCode,
      environment: isProd ? "production" : "sandbox",
      logLevel: isProd ? "error" : "verbose",
      // e：内部事件名称，可忽略
      // attribution: 发生变更的归因详情
      // 重新安装才会触发, 删除 store，刷新就会了。
      // attributionCallback: (e, attribution) => {
      //   console.log("sk--, adjust callback", e, attribution);
      // },
    });
    this.isInitAdjust = true;
  },

  initFBPixel(): void {
    if (this.isInitFbPX) return;
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const fbq = require("react-facebook-pixel").default;
    fbq.init(facebookPixelCode, undefined, { debug: !isProd, autoConfig: false });
    fbq.pageView();
    this.isInitFbPX = true;
  },

  initGA(): void {
    if (this.isInitGA) return;
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const ga = require("react-ga4").default;
    ga.initialize(googleAnalyticsCode, { debug: !isProd, gaOptions: { send_page_view: true } });
    ga.send("pageview");
    this.isInitGA = true;
  },

  // 登录之后是需要设置uid的，如果没有登录，则不需要设置uid
  setUid(uid: string): void {
    this.uid = uid;
  },

  init(): void {
    this.initAdjust();
    this.initFBPixel();
    this.initGA();
    // sdk 初始化完毕，清理队列任务。
    setTimeout(() => {
      while (this.eventTasks.length) {
        const event = this.eventTasks.shift();
        event && this.trackEvent(event.eventName, event.eventProperties);
      }
    }, 0);
  },

  checkAdIdAndDeviceId(): void {
    if (!this.adjust_id) {
      this.adjust_id = window.Adjust?.getAttribution?.()?.adid || "";
    }

    if (!this.deviceId) {
      this.deviceId = window.Adjust?.getWebUUID?.() || "";
    }
  },

  // 有选择 cookie 才队列事件
  checkInitCookieKey(): boolean {
    const cookieVal = JsCookie.get(COOKIE_KEYS_MAP.AcceptCookieKey);
    if (cookieVal === undefined || cookieVal === "") {
      return false;
    }
    return true;
  },

  trackEvent(eventName: AdjustConfigKey | FbPXConfigKey | GAConfigKey, eventProperties: PropertyType): void {
    this.checkAdIdAndDeviceId();
    if (!this.isInitAdjust && !this.isInitFbPX && !this.isInitGA) {
      // 放到临时队列，
      this.checkInitCookieKey() && this.eventTasks.push({ eventName, eventProperties });
      return;
    }
    this.trackAdjustEvent(eventName as AdjustConfigKey, eventProperties);
    this.trackFBPixelEvent(eventName as FbPXConfigKey, eventProperties);
    this.trackGAEvent(eventName as GAConfigKey, eventProperties);
  },

  trackAdjustEvent(eventName: AdjustConfigKey, eventProperties: PropertyType): void {
    if (!isValidKey(eventName, adjustEventConfig)) {
      // eslint-disable-next-line no-console
      this.debug && console.warn(`adjust event [${eventName}] is not exit`);
      return;
    }
    this.checkAdIdAndDeviceId();
    const event = adjustEventConfig[eventName];
    // 基准 key 是按照 adjust 来的，有个对应字段需要转换一下 key
    const keyMap: Record<string, string> = { revenue: "value", item_id: "items" };
    // 这俩 key 要放在外层.
    const specialKey = ["revenue", "currency"];
    // 这些不用用户传，直接用默认值
    const innerKey = ["adjust_id", "uid", "deviceId"];

    const params: any = {};
    for (const key of Object.keys(event.eventProperties)) {
      // 先取内置值。
      if (innerKey.includes(key)) {
        if (params.partnerParams === undefined) {
          params.partnerParams = [];
        }
        const value = this[key as "adjust_id" | "uid" | "deviceId"];
        if (value) {
          params.partnerParams.push({ key, value });
          continue;
        }
      }
      let result = eventProperties[(keyMap[key] || key) as keyof PropertyType] as any;
      if (key === "currency") {
        if (eventProperties.countryId === undefined) {
          // eslint-disable-next-line no-console
          this.debug && console.warn(`货币符号必须传入国家 ID`);
          continue;
        } else {
          result = isoCurrencyConfig[eventProperties.countryId];
        }
      }

      // 警告用户.
      if (result === undefined) {
        // eslint-disable-next-line no-console
        this.debug && console.warn(`event property key '${key}' is must be not null`);
        continue;
      }
      // 商品信息转换一下。
      if (key === "item_id" && Array.isArray(result)) {
        result = result.map((product) => product.item_id);
      }

      // 再取用户传的值.
      if (specialKey.includes(key)) {
        params[key] = result;
      } else {
        if (params.partnerParams === undefined) {
          params.partnerParams = [];
        }
        params.partnerParams.push({ key, value: result });
      }
    }

    // eslint-disable-next-line no-console
    // console.log("sk--, adjust event", this.deviceId, this.adjust_id, { eventToken: event.eventToken, ...params });

    Adjust.trackEvent({ eventToken: event.eventToken, ...params });
  },

  trackFBPixelEvent(eventName: FbPXConfigKey, eventProperties: PropertyType): void {
    if (!isValidKey(eventName, fbPXEventConfig)) {
      // eslint-disable-next-line no-console
      this.debug && console.warn(`facebook pixel event [${eventName}] is not exit`);
      return;
    }
    this.checkAdIdAndDeviceId();
    const event = fbPXEventConfig[eventName];
    // 基准 key 是按照 adjust 来的，有个对应字段需要转换一下 key
    const keyMap: Record<string, string> = { content_ids: "items" };
    // 这个 key 不用做任何处理.
    const specialKey = ["content_type"];
    // 这些不用用户传，直接用默认值
    const innerKey = ["adjust_id", "uid", "deviceId"];
    const params: any = {};
    for (const key of Object.keys(event.eventProperties)) {
      // 先取内置值。
      if (innerKey.includes(key)) {
        const value = this[key as "adjust_id" | "uid" | "deviceId"];
        if (value) {
          params[key] = value;
          continue;
        }
      }
      // 直接取配置值。
      if (specialKey.includes(key)) {
        params[key] = (event.eventProperties as Record<string, string>)[key];
        continue;
      }
      let result = eventProperties[(keyMap[key] || key) as keyof PropertyType] as any;
      if (key === "currency") {
        if (eventProperties.countryId === undefined) {
          // eslint-disable-next-line no-console
          this.debug && console.warn(`货币符号必须传入国家 ID`);
          continue;
        } else {
          result = isoCurrencyConfig[eventProperties.countryId];
        }
      }
      // 警告用户.
      if (result === undefined) {
        // eslint-disable-next-line no-console
        this.debug && console.warn(`event property key '${key}' is must be not null`);
        continue;
      }

      // 商品信息转换一下。
      if (key === "content_ids" && Array.isArray(result)) {
        result = result.map((product) => product.item_id);
      }

      // 再取用户传的值.
      params[key] = result;
    }

    // eslint-disable-next-line no-console
    // console.log("sk--, fb pixel event", event.eventName, params);

    fbq("track", event.eventName, params);
  },

  trackGAEvent(eventName: GAConfigKey, eventProperties: PropertyType): void {
    if (!isValidKey(eventName, gaEventConfig)) {
      // eslint-disable-next-line no-console
      this.debug && console.warn(`Google Analytics event [${eventName}] is not exit`);
      return;
    }
    this.checkAdIdAndDeviceId();
    // 基准 key 是按照 adjust 来的，有个对应字段需要转换一下 key
    const keyMap: Record<string, string> = { item_id: "items" };
    // 这些不用用户传，直接用默认值
    const innerKey = ["adjust_id", "uid", "deviceId"];
    const event = gaEventConfig[eventName];
    const params: any = {};
    for (const key of Object.keys(event.eventProperties)) {
      // 先取内置值。
      if (innerKey.includes(key)) {
        const value = this[key as "adjust_id" | "uid" | "deviceId"];
        if (value) {
          params[key] = value;
          continue;
        }
      }
      let result = eventProperties[(keyMap[key] || key) as keyof PropertyType];
      if (key === "currency") {
        if (eventProperties.countryId === undefined) {
          // eslint-disable-next-line no-console
          this.debug && console.warn(`货币符号必须传入国家 ID`);
          continue;
        } else {
          result = isoCurrencyConfig[eventProperties.countryId];
        }
      }
      // 警告用户.
      if (result === undefined) {
        // eslint-disable-next-line no-console
        this.debug && console.warn(`event property key '${key}' is must be not null`);
        continue;
      }

      // 商品信息转换一下。
      if (key === "item_id" && Array.isArray(result)) {
        result = result.map((product) => product.item_id).join(",");
      }

      // 再取用户传的值.
      params[key] = result;
    }

    // eslint-disable-next-line no-console
    // console.log("sk--, ga event", event.eventName, params);

    gtag("event", event.eventName, params);
  },
};

export default Analytics;
